<template>
  <layout-report-step
    :isDone="valid"
    :title="configuration.header.title"
    :description="configuration.header.description"
    :image="configuration.header.image"
    :debug="configuration.header.debug"
    :hasInstructions="instruction ? true : false"
    :instructionPictures="configuration.instruction.pictures"
    :instructionText="configuration.instruction.text"
    :instructionTitle="configuration.instruction.title"
  >
    <v-radio-group v-model="isDamage" :label="$t('report.imageDamageOptional.isDamage.question')">
      <v-radio :label="$t('report.imageDamageOptional.isDamage.false')" :value="false"></v-radio>
      <v-radio :label="$t('report.imageDamageOptional.isDamage.true')" :value="true"></v-radio>
    </v-radio-group>
    <v-row fill-height v-if="isDamage">
      <v-col cols="12" md="12" sm="12" xs="12">
        <v-textarea
          clearable
          auto-grow
          outlined
          :label="$t('report.imageDamageOptional.isDamage.placeHolderText')"
          v-model.lazy="message"
          @blur="updateMessage"
          class="contain padded"
          :counter="counter"
          rows="7"
          data-test-form-description
        ></v-textarea>
      </v-col>
      <v-col cols="12">
        <file-upload-preview
          :example="configuration.exampleImages.damageDetail.example"
          :displayFiles="files"
          :asset="configuration.exampleImages.damageDetail.overlay"
          :instruction="instruction"
          :onDelete="handleDelete"
          :onUpload="handleUpload"
        />
      </v-col>
    </v-row>
  </layout-report-step>
</template>

<script lang="ts">
import FileUploadPreview from "@/components/utility/FileUploadPreview.vue";
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { IViewImageUploadable } from "@/lib/interfaces/Report/IViewImageUploadable";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import ReportImageSubmission from "@/mixins/ReportImageSubmissionMixin.vue";
import { IImageUploaded } from "@/models/Image/IImageUploaded";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportType } from "@/store/enum/reportType";
import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";

@Component({
  components: {
    LayoutReportStep,
    FileUploadPreview
  }
})
export default class ReportImageDamageOptional
  extends ReportImageSubmission
  implements IReportStep, IViewImageUploadable
{
  header: IReportHeaderData = this.configuration.header;
  instruction: IReportInstructionData = this.configuration.instruction;

  screen = ReportScreenEnum.damageimageoptional;

  get isDamage() {
    return ReportModule.isDamage;
  }

  set isDamage(isDamage: boolean) {
    ReportModule.setIsDamage(isDamage);
  }

  get valid(): boolean {
    if (!this.isDamage) {
      return true;
    }

    return this.isDone;
  }

  internalMessage = "";
  counter = 1000;

  get message(): string {
    return ReportModule.inCreationReport.message;
  }

  set message(m: string) {
    this.internalMessage = m;
  }

  updateMessage(e: any) {
    ReportModule.setMessage(e.target.value);
  }

  /**
   * @inheritdoc
   */
  async handleUpload(file: File): Promise<boolean> {
    await ReportModule.addDamageDetail(file);
    return true;
  }

  /**
   * @inheritdoc
   */
  async handleDelete(file: File): Promise<boolean> {
    await ReportModule.removeDamageDetail(file);
    return true;
  }

  /**
   * @inheritdoc
   */
  get files(): IImageUploaded[] {
    return ReportModule.damagesDetail;
  }

  get configuration() {
    switch (ReportModule.reportType) {
      case ReportType.CAR:
        return {
          exampleImages: {
            damageDetail: {
              example: AssetEnum.damageDetail,
              overlay: AssetEnum.damageDetailOutline
            }
          },
          header: {
            title: "report.imageDamageOptional.car.title",
            description: "report.imageDamageOptional.car.description",
            image: AssetEnum.damageDetailExample,
            debug: false
          },
          instruction: {
            title: "report.imageDamageOptional.car.title",
            text: "report.imageDamageOptional.car.instructionText",
            pictures: [AssetEnum.damageDetailInstruction],
            example: AssetEnum.damageDetailExample,
            display: true
          }
        };
      case ReportType.MOTORBIKE:
        return {
          exampleImages: {
            damageDetail: {
              example: AssetEnum.noImage,
              overlay: ""
            }
          },
          header: {
            title: "report.imageDamageOptional.motorbike.title",
            description: "report.imageDamageOptional.motorbike.description",
            image: AssetEnum.noImage,
            debug: false
          },
          instruction: {
            title: "report.imageDamageOptional.motorbike.title",
            text: "report.imageDamageOptional.motorbike.instructionText",
            pictures: [AssetEnum.damageDetailInstruction],
            example: AssetEnum.noImage,
            display: true
          }
        };

      default:
        return {
          exampleImages: {
            damageDetail: {
              example: AssetEnum.damageDetail,
              overlay: AssetEnum.damageDetailOutline
            }
          },
          header: {
            title: "report.imageDamageOptional.car.title",
            description: "report.imageDamageOptional.car.description",
            image: AssetEnum.damageDetailExample,
            debug: false
          },
          instruction: {
            title: "report.imageDamageOptional.car.title",
            text: "report.imageDamageOptional.car.instructionText",
            pictures: [AssetEnum.damageDetailInstruction],
            example: AssetEnum.damageDetailExample,
            display: true
          }
        };
    }
  }
}
</script>

<style>
.dialogBar {
  width: 100vw !important;
  position: absolute !important;
  left: 0 !important;
  top: 0;
}
</style>
